import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormLabel from "@mui/material/FormLabel";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { api, unwrap, type Role, type UserSummary } from "../api/client";
import { useSession } from "../auth/session";

const ROLES: Role[] = [
  "Viewer",
  "Analyst",
  "CaseManager",
  "TeamLead",
  "Importer",
  "Administrator",
  "Auditor",
  "RecordsOfficer",
];

/** The desktop app's two kinds of account (simple mode); as `avgv_api::desktop`. */
const ADMIN_ROLES: Role[] = [
  "Administrator",
  "RecordsOfficer",
  "Auditor",
  "TeamLead",
  "CaseManager",
  "Importer",
];
const USER_ROLES: Role[] = ["CaseManager", "Importer"];

const isAdmin = (roles: Role[]) => roles.includes("Administrator");

export function rolesLabel(roles: Role[], simple: boolean) {
  if (simple) return isAdmin(roles) ? "Administrator" : "User";
  return roles.join(", ");
}

/** Roles to choose: every role, or in simple mode Administrator or User. */
function RolePicker({
  roles,
  simple,
  onChange,
  size,
}: {
  roles: Role[];
  simple: boolean;
  onChange: (r: Role[]) => void;
  size?: "small";
}) {
  if (simple) {
    return (
      <RadioGroup
        row
        value={isAdmin(roles) ? "admin" : "user"}
        onChange={(e) =>
          onChange(e.target.value === "admin" ? ADMIN_ROLES : USER_ROLES)
        }
      >
        <FormControlLabel
          value="user"
          control={<Radio size={size} />}
          label="User: view, import, analyse, cases and exports"
        />
        <FormControlLabel
          value="admin"
          control={<Radio size={size} />}
          label="Administrator: also users, settings and the audit log"
        />
      </RadioGroup>
    );
  }
  const toggle = (r: Role) =>
    onChange(roles.includes(r) ? roles.filter((x) => x !== r) : [...roles, r]);
  return (
    <FormGroup row>
      {ROLES.map((r) => (
        <FormControlLabel
          key={r}
          control={
            <Checkbox
              size={size}
              checked={roles.includes(r)}
              onChange={() => toggle(r)}
            />
          }
          label={r}
        />
      ))}
    </FormGroup>
  );
}

export const USERS_KEY = ["users"] as const;

export function useUsers() {
  return useQuery({
    queryKey: USERS_KEY,
    queryFn: () => unwrap(api.GET("/api/users")),
  });
}

export function UsersPage() {
  const users = useUsers();
  const session = useSession().data;
  const me = session?.user.id;
  const simple = session?.simple ?? false;
  const desktop = session?.desktop ?? false;
  return (
    <Stack spacing={3} component="section">
      <Typography variant="h5" component="h2">
        Users
      </Typography>
      <CreateUserForm simple={simple} />
      {users.isError && <Alert severity="error">{users.error.message}</Alert>}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {users.data?.map((u) => (
              <UserRow
                key={u.id}
                user={u}
                isMe={u.id === me}
                simple={simple}
                desktop={desktop}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

function UserRow({
  user,
  isMe,
  simple,
  desktop,
}: {
  user: UserSummary;
  isMe: boolean;
  simple: boolean;
  desktop: boolean;
}) {
  const signOut = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/users/{id}/sign-out", {
          params: { path: { id: user.id } },
        }),
      ),
  });
  const resetMfa = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/users/{id}/mfa/reset", {
          params: { path: { id: user.id } },
        }),
      ),
  });
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Role[] | null>(null);
  const save = useMutation({
    mutationFn: (roles: Role[]) =>
      unwrap(
        api.PUT("/api/users/{id}/roles", {
          params: { path: { id: user.id } },
          body: { roles },
        }),
      ),
    onSuccess: () => {
      setEditing(null);
      void qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
  const [resetting, setResetting] = useState(false);
  const setDisabled = useMutation({
    mutationFn: (disabled: boolean) =>
      unwrap(
        api.PUT("/api/users/{id}/disabled", {
          params: { path: { id: user.id } },
          body: { disabled },
        }),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: USERS_KEY }),
  });
  return (
    <TableRow>
      <TableCell>{user.username}</TableCell>
      <TableCell>{user.display_name}</TableCell>
      <TableCell>
        {editing ? (
          <Stack spacing={1}>
            <RolePicker
              roles={editing}
              simple={simple}
              onChange={setEditing}
              size="small"
            />
            {save.isError && (
              <Alert severity="error">{save.error.message}</Alert>
            )}
            <Stack direction="row" sx={{ gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                disabled={save.isPending}
                onClick={() => save.mutate(editing)}
              >
                Save roles
              </Button>
              <Button size="small" onClick={() => setEditing(null)}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        ) : (
          rolesLabel(user.roles, simple)
        )}
      </TableCell>
      <TableCell>{user.disabled ? "Disabled" : "Active"}</TableCell>
      <TableCell>{new Date(user.created_at).toLocaleString()}</TableCell>
      <TableCell>
        {!editing && !isMe && (
          <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
            <Button size="small" onClick={() => setEditing(user.roles)}>
              Edit roles
            </Button>
            <Button size="small" onClick={() => setResetting(true)}>
              Reset password
            </Button>
            <Button
              size="small"
              disabled={signOut.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    `Sign ${user.username} out everywhere, and forget their remembered browsers?`,
                  )
                )
                  signOut.mutate();
              }}
            >
              {signOut.isSuccess ? "Signed out" : "Sign out everywhere"}
            </Button>
            {!desktop && (
              <Button
                size="small"
                color="error"
                disabled={resetMfa.isPending}
                onClick={() => {
                  if (
                    window.confirm(
                      `Reset ${user.username}'s two-factor sign-in? They are signed out and set it up again at their next sign-in. Do this only once you are sure who is asking.`,
                    )
                  )
                    resetMfa.mutate();
                }}
              >
                {resetMfa.isSuccess ? "Two-factor reset" : "Reset two-factor"}
              </Button>
            )}
            <Button
              size="small"
              color={user.disabled ? "primary" : "error"}
              disabled={setDisabled.isPending}
              onClick={() => {
                if (
                  user.disabled ||
                  window.confirm(
                    `Disable ${user.username}? They are signed out at once and can't sign in until enabled again.`,
                  )
                )
                  setDisabled.mutate(!user.disabled);
              }}
            >
              {user.disabled ? "Enable" : "Disable"}
            </Button>
          </Stack>
        )}
        {setDisabled.isError && (
          <Alert severity="error">{setDisabled.error.message}</Alert>
        )}
        {resetting && (
          <ResetPasswordDialog
            user={user}
            onClose={() => setResetting(false)}
          />
        )}
      </TableCell>
    </TableRow>
  );
}

function ResetPasswordDialog({
  user,
  onClose,
}: {
  user: UserSummary;
  onClose: () => void;
}) {
  const [password, setPassword] = useState("");
  const reset = useMutation({
    mutationFn: () =>
      unwrap(
        api.PUT("/api/users/{id}/password", {
          params: { path: { id: user.id } },
          body: { temporary_password: password },
        }),
      ),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    reset.mutate();
  };
  return (
    <Dialog open onClose={onClose} component="form" onSubmit={submit}>
      <DialogTitle>Reset {user.username}&apos;s password</DialogTitle>
      <DialogContent>
        {reset.isSuccess ? (
          <Alert severity="success">
            Done. Give {user.display_name} the temporary password in person or
            by another secure route; they must choose their own when they next
            sign in.
          </Alert>
        ) : (
          <Stack spacing={2}>
            <DialogContentText>
              {user.display_name} is signed out everywhere and must choose a new
              password the next time they sign in.
            </DialogContentText>
            <TextField
              label="Temporary password"
              helperText="At least 12 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              slotProps={{ htmlInput: { minLength: 12 } }}
              required
              autoFocus
            />
            {reset.isError && (
              <Alert severity="error">{reset.error.message}</Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {reset.isSuccess ? "Close" : "Cancel"}
        </Button>
        {!reset.isSuccess && (
          <Button type="submit" variant="contained" disabled={reset.isPending}>
            Reset password
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function CreateUserForm({ simple }: { simple: boolean }) {
  const qc = useQueryClient();
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [roles, setRoles] = useState<Role[]>(simple ? USER_ROLES : ["Analyst"]);

  const create = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/users", {
          body: {
            username,
            display_name: displayName,
            roles,
            temporary_password: password,
          },
        }),
      ),
    onSuccess: () => {
      setUsername("");
      setDisplayName("");
      setPassword("");
      void qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };

  return (
    <Paper component="form" variant="outlined" onSubmit={submit} sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h6" component="h3">
          New user
        </Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
          <TextField
            label="Display name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
          />
          <TextField
            label="Temporary password"
            type="password"
            autoComplete="new-password"
            slotProps={{ htmlInput: { minLength: 12 } }}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </Stack>
        <div>
          <FormLabel component="legend">
            {simple ? "Account" : "Roles"}
          </FormLabel>
          <RolePicker roles={roles} simple={simple} onChange={setRoles} />
        </div>
        {create.isError && (
          <Alert severity="error">{create.error.message}</Alert>
        )}
        <div>
          <Button type="submit" variant="contained" disabled={create.isPending}>
            Create user
          </Button>
        </div>
      </Stack>
    </Paper>
  );
}
